"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { Menu, X } from "lucide-react";
import { ZoblissLogo } from "@/components/icons/common";
import { cn } from "@/lib/utils";
import { HELP_CENTER_PATH, KB_BASE_PATH } from "../paths";
import { HelpSearch } from "./HelpSearch";

const NAV_LINKS = [
  { href: HELP_CENTER_PATH, label: "Home" },
  { href: KB_BASE_PATH, label: "Knowledge base" },
];

// The help center's own top bar. It behaves like the landing header: it sits
// on the warm background at the top of the page, turns white once the page
// scrolls, and folds into a full-screen menu on smaller screens. There's a
// search box on every page except the help center home, which has its own.
export function HelpCenterHeader() {
  const pathname = usePathname();
  const { data: session } = useSession();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isHome = pathname === HELP_CENTER_PATH;
  const accountLink = session
    ? {
        href: session.role === "super_admin" ? "/admin/home" : "/calendar",
        label: "Dashboard",
      }
    : { href: "/login", label: "Log in" };
  const isActive = (href) =>
    href === HELP_CENTER_PATH
      ? isHome
      : pathname === href || pathname.startsWith(`${href}/`);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Keep the page still behind the open menu.
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  return (
    <header
      className={cn(
        "sticky inset-x-0 top-0 z-30 w-full transition-all",
        isScrolled ? "bg-white" : "bg-white pt-4 md:pt-7",
      )}
    >
      <div
        className={cn(
          "absolute inset-0 top-7 block rounded-[26px] transition-all duration-200",
          isScrolled
            ? "border-b border-neutral-100 bg-white/75 backdrop-blur-lg"
            : "border-b border-transparent bg-transparent",
        )}
      />
      <div className="relative z-30 mx-1.5 max-w-7xl rounded-[26px] bg-white px-3 md:mx-auto md:w-full lg:px-4 lg:py-2">
        <div className="flex h-14 items-center justify-between gap-2 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3">
            <Link href="/" aria-label="Zobliss home">
              <ZoblissLogo className="h-auto w-24 md:w-32" />
            </Link>
            <span className="h-6 w-px bg-neutral-200" aria-hidden />
            <Link
              href={HELP_CENTER_PATH}
              className="text-sm whitespace-nowrap text-neutral-800 hover:text-neutral-900 md:text-base"
            >
              Help center
            </Link>
          </div>

          <nav
            aria-label="Help center"
            className="hidden flex-1 items-center gap-1 lg:flex"
          >
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                aria-current={isActive(link.href) ? "page" : undefined}
                className="relative rounded-lg px-4 py-2 text-base font-medium text-neutral-800 transition-colors hover:bg-neutral-900/5 hover:text-neutral-900"
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="bg-neutral-600 absolute top-full left-1/2 mt-1 h-1 w-[17px] -translate-x-1/2 rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-1 md:gap-3 lg:flex-1">
            {!isHome && <HelpSearch className="hidden max-w-sm lg:block" />}
            <Link
              href={accountLink.href}
              className="text-content-emphasis hover:bg-muted flex h-9 items-center rounded-lg border border-[#11110d52] bg-white px-4 text-sm font-medium whitespace-nowrap md:h-11"
            >
              {accountLink.label}
            </Link>
            <button
              type="button"
              className="rounded-full p-2 transition-colors duration-200 hover:bg-neutral-200 focus:outline-none active:bg-neutral-300 lg:hidden"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label="Toggle help center menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <X className="size-5 text-neutral-600" />
              ) : (
                <Menu className="size-5 text-neutral-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Smaller screens: the search and tabs open in a full-screen menu. */}
      <nav
        aria-label="Help center menu"
        className={cn(
          "fixed inset-0 z-20 max-h-screen w-full overflow-y-auto bg-white px-5 pt-24 pb-16 lg:hidden",
          isMenuOpen ? "block" : "hidden",
        )}
      >
        {!isHome && <HelpSearch className="mb-4" />}
        <ul className="grid divide-y divide-neutral-200">
          {NAV_LINKS.map((link) => (
            <li key={link.href} className="py-3">
              <Link
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive(link.href) ? "page" : undefined}
                className={cn(
                  "flex w-full font-medium",
                  isActive(link.href) &&
                    "decoration-neutral-600 underline decoration-2 underline-offset-8",
                )}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
}
